import React, { useEffect, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Input, message, Space, Table, Tabs, Tag } from 'antd';
import { SaveOutlined, EditOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useUserApi } from '../../../api/user/userApp';
import { TPassword, TUserApp, TUserappLog } from '../../../models/user/User';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { CODIFICATION_ROLES } from '../../../constant/codification';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

const PasswordList: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { getPasswordsByUserAppId, createPassword, updatePassword, deletePassword, getUserLogsByUserId, getUserAppById } = useUserApi();
    const { isRoleExist } = useTilmiAuth();
    const canViewLogs = isRoleExist(CODIFICATION_ROLES.ACTIVATE_DEACTIVATE);
    const [passwords, setPasswords] = useState<TPassword[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [addingId, setAddingId] = useState<number>(0);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);
    const [logs, setLogs] = useState<TUserappLog[]>([]);
    const [logsLoading, setLogsLoading] = useState<boolean>(false);
    const [userLoginMap, setUserLoginMap] = useState<Record<number, string>>({});
    const [activeTab, setActiveTab] = useState<string>('passwords');

    const userapp: TUserApp = location.state?.userapp;

    useEffect(() => {
        if (!userapp) {
            message.error('No userapp selected');
            return;
        }
        setIsLoading(true);
        getPasswordsByUserAppId(userapp.id)
            .then((resp) => {
                setPasswords(resp.data);
            })
            .catch((err) => {
                message.error(err?.response?.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line
    }, [userapp]);

    useEffect(() => {
        if (!userapp || activeTab !== 'logs' || !canViewLogs) return;
        setLogsLoading(true);
        getUserLogsByUserId(userapp.id)
            .then(async (resp) => {
                const data = resp.data || [];
                setLogs(data);
                // Build user login map from distinct IDs in logs
                const ids = Array.from(
                    new Set(
                        data.flatMap((l) => [l.userapp_id, l.userapp_id_concerned]).filter((v) => typeof v === 'number') as number[]
                    )
                );
                if (ids.length > 0) {
                    try {
                        const results = await Promise.all(
                            ids.map(async (id) => {
                                try {
                                    const res = await getUserAppById(id);
                                    return [id, res.data?.login as string] as const;
                                } catch {
                                    return [id, String(id)] as const;
                                }
                            })
                        );
                        const map: Record<number, string> = {};
                        results.forEach(([id, login]) => (map[id] = login));
                        setUserLoginMap(map);
                    } catch (e: any) {
                        console.warn('Failed to resolve user logins', e?.message || e);
                    }
                }
            })
            .catch((err) => message.error(err?.response?.data ?? err.message))
            .finally(() => setLogsLoading(false));
    }, [userapp, activeTab, canViewLogs]);

    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingPassword, setEditingPassword] = useState<TPassword | null>(null);

    const edit = (password: TPassword) => {
        setEditingKey(password.id);
        setEditingPassword({ ...password, password: "" });
    };

    const cancel = () => {
        const idRow = editingPassword?.id || 0;
        if (idRow <= 0) {
            setPasswords((prev) => [...prev ?? []].filter(e => e.id !== idRow));
        }
        setEditingKey(null);
        setEditingPassword(null);
    };

    const handleAddPassword = () => {
        const idRow = addingId - 1;
        const newPassword: TPassword = {
            id: idRow,
            userapp_id: userapp.id,
            userapp: userapp,
            password: '',
            start_time: new Date().toISOString(),
            end_time: undefined
        };

        setAddingId(idRow);
        setEditingKey(idRow);
        setEditingPassword(newPassword);
        setPasswords((prev) => [newPassword, ...(prev || [])]);
    };

    const save = (id: number) => {
        if (editingPassword) {
            setIsLoading(true);
            (editingPassword.id > 0
                ? updatePassword(editingPassword)
                : createPassword(editingPassword))
                .then((resp) => {
                    const passwordResult: TPassword = resp.data;
                    const updatedPasswords = passwords.map((password) =>
                        password.id === id ? passwordResult : password
                    );
                    setPasswords(updatedPasswords);
                    setEditingKey(null);
                    setEditingPassword(null);
                    message.success(t('message.savedSuccessfully'));
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;
        setDeleteLoading(true);
        deletePassword(deleteId)
            .then(() => {
                setPasswords(passwords.filter((password) => password.id !== deleteId));
                message.success(t('message.deletedSuccessfully'));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };

    const columns: ColumnsType<TPassword> = [
        {
            title: 'Password',
            dataIndex: 'password',
            key: 'password',
            render: (text: string, record: TPassword) =>
                editingKey === record.id ? (
                    <Input
                        value={editingPassword?.password}
                        onChange={(e) => {
                            if (editingPassword) {
                                setEditingPassword({ ...editingPassword, password: e.target.value });
                            }
                        }}
                    />
                ) : (
                    '••••••••'
                ),
        },
        {
            title: 'Time Range',
            key: 'time_range',
            render: (_: any, record: TPassword) =>
                editingKey === record.id ? (
                    <RangePicker
                        showTime
                        format="YYYY-MM-DD HH:mm:ss"
                        value={
                            editingPassword?.start_time
                                ? [
                                    dayjs(editingPassword.start_time),
                                    editingPassword.end_time ? dayjs(editingPassword.end_time) : null,
                                ]
                                : null
                        }
                        onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
                            if (dates) {
                                const [start, end] = dates;
                                setEditingPassword({
                                    ...editingPassword!,
                                    start_time: start ? start.toISOString() : '',
                                    end_time: end ? end.toISOString() : undefined,
                                });
                            } else {
                                setEditingPassword({
                                    ...editingPassword!,
                                    start_time: '',
                                    end_time: undefined,
                                });
                            }
                        }}
                    />
                ) : (
                    `${dayjs(record.start_time).format('DD/MM/YYYY HH:mm:ss')} - ${record.end_time ? dayjs(record.end_time).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}`
                ),
        },
        {
            title: 'Action',
            key: 'actions',
            fixed: 'right' as 'right',
            width: 220,
            render: (_: any, record: TPassword) => (
                <Space size="middle">
                    {editingKey === record.id ? (
                        <>
                            <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                            <Button icon={<StopOutlined />} onClick={cancel} />
                        </>
                    ) : (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                            <Button icon={<DeleteOutlined />} danger onClick={() => confirmDelete(record.id)} loading={deleteId === record.id && deleteLoading} />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    const logColumns: ColumnsType<TUserappLog> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('text.action') || 'Action',
            dataIndex: 'code_action',
            key: 'code_action',
            render: (code: string) => <Tag>{code}</Tag>,
        },
        {
            title: t('text.actor') || 'Actor',
            dataIndex: 'userapp_id',
            key: 'userapp_id',
            width: 160,
            render: (id: number) => userLoginMap[id] || String(id),
        },
        {
            title: t('text.concerned') || 'Concerned',
            dataIndex: 'userapp_id_concerned',
            key: 'userapp_id_concerned',
            width: 180,
            render: (id: number) => userLoginMap[id] || String(id),
        },
        {
            title: t('text.date') || 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
        },
    ];

    const renderContent = () => {
        if (canViewLogs) {
            return (
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <Tabs.TabPane tab="Passwords" key="passwords">
                        <Space style={{ marginBottom: 16 }}>
                            <Button type="primary" onClick={handleAddPassword}>
                                {t('button.add')}
                            </Button>
                        </Space>
                        <Table<TPassword>
                            columns={columns}
                            dataSource={passwords}
                            rowKey="id"
                            loading={loading}
                            pagination={{ pageSize: 20 }}
                            scroll={{ x: 500 }}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={t('text.logs') || 'Logs'} key="logs">
                        <Table<TUserappLog>
                            columns={logColumns}
                            dataSource={logs}
                            rowKey="id"
                            loading={logsLoading}
                            pagination={{ pageSize: 20 }}
                        />
                    </Tabs.TabPane>
                </Tabs>
            );
        } else {
            return (
                <>
                    <Space style={{ marginBottom: 16 }}>
                        <Button type="primary" onClick={handleAddPassword}>
                            {t('button.add')}
                        </Button>
                    </Space>
                    <Table<TPassword>
                        columns={columns}
                        dataSource={passwords}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                        scroll={{ x: 500 }}
                    />
                </>
            );
        }
    };

    return (
        <AppLayout 
            title={`User : ${userapp?.login}`} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            {renderContent()}

            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title="Confirm Deletion"
                description="Are you sure you want to delete this password? This action is irreversible."
            />
        </AppLayout>
    );
};

export default PasswordList;
